<template>
  <div class="person-node" :class="[typeClass, { blocked, deceased: isDeceased, 'has-share': share !== null }]">
    <!-- Icon wrapper (for share overlay positioning) -->
    <div class="icon-wrap">
      <!-- Person silhouette icon -->
      <svg class="person-icon" viewBox="0 0 32 40" fill="currentColor">
        <circle cx="16" cy="10" r="7" />
        <path d="M4 36c0-6.627 5.373-12 12-12s12 5.373 12 12" />
      </svg>
      <!-- Share circle overlaid on icon -->
      <span v-if="share !== null && !blocked" class="share-circle">
        {{ formatShare(share) }}
      </span>
    </div>
    <!-- Label -->
    <span class="person-label">{{ node.label }}</span>
    <span v-if="blocked" class="blocked-label">blocked</span>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  node: { type: Object, required: true },
  share: { type: Number, default: null },
  blocked: { type: Boolean, default: false },
  isDeceased: { type: Boolean, default: false },
})

const typeClass = computed(() => {
  if (props.isDeceased) return 'type-deceased'
  return props.node.gender === 'male' ? 'type-male' : 'type-female'
})

function formatShare(val) {
  return val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)
}
</script>

<style scoped>
.person-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 56px;
  max-width: 72px;
  transition: opacity 0.2s ease;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Icon wrapper — positions the share circle over the silhouette */
.icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Person icon */
.person-icon {
  width: 28px;
  height: 34px;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}

/* When share is showing, fade the silhouette so the circle pops */
.has-share .person-icon {
  opacity: 0.2;
}

/* Gender colors */
.type-male .person-icon { color: #475569; }
.type-female .person-icon { color: #78716c; }
.type-deceased .person-icon { color: #dc2626; }

/* Label */
.person-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--color-gray-700, #374151);
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
}

.type-deceased .person-label {
  font-weight: 700;
  color: #dc2626;
}

/* ── Share circle: overlaid on the person icon ── */
.share-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: #2563eb;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.4);
  animation: popIn 0.3s ease-out;
  white-space: nowrap;
}

/* Female heirs get a different accent */
.type-female .share-circle {
  background: #db2777;
  box-shadow: 0 1px 4px rgba(219, 39, 119, 0.4);
}

@keyframes popIn {
  from { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}

/* Blocked state */
.blocked {
  opacity: 0.35;
}

.blocked .person-icon {
  color: #9ca3af;
}

.blocked .person-label {
  text-decoration: line-through;
  color: #9ca3af;
}

.blocked-label {
  font-size: 8px;
  color: #9ca3af;
  font-style: italic;
}
</style>
